/* LensAdvisor documentation pages — accordion height animation + sticky nav + reveal + copy button. */
(function () {
  let stickyState = null;

  const measureHeader = () => {
    const header = document.querySelector('#header-group');
    const h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--la-site-header-h', h + 'px');
    return h;
  };

  const initStickyNav = () => {
    const nav = document.querySelector('.la-docs-nav');
    if (!nav) return;
    if (stickyState && stickyState.nav === nav) return;
    if (stickyState) {
      window.removeEventListener('scroll', stickyState.onScroll);
      window.removeEventListener('resize', stickyState.onResize);
      if (stickyState.placeholder && stickyState.placeholder.parentNode) {
        stickyState.placeholder.parentNode.removeChild(stickyState.placeholder);
      }
    }

    const placeholder = document.createElement('div');
    placeholder.className = 'la-docs-nav-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    nav.parentNode.insertBefore(placeholder, nav);

    let headerHeight = measureHeader();
    let navTop = 0;

    const measure = () => {
      const wasSticky = nav.classList.contains('is-sticky');
      if (wasSticky) nav.classList.remove('is-sticky');
      placeholder.style.display = 'none';
      const rect = nav.getBoundingClientRect();
      navTop = rect.top + window.scrollY;
      const cs = window.getComputedStyle(nav);
      placeholder.style.height = rect.height + 'px';
      placeholder.style.marginTop = cs.marginTop;
      placeholder.style.paddingTop = cs.paddingTop;
      placeholder.style.borderTopWidth = cs.borderTopWidth;
      placeholder.style.borderTopStyle = 'solid';
      placeholder.style.borderTopColor = 'transparent';
      if (wasSticky) {
        nav.classList.add('is-sticky');
        placeholder.style.display = 'block';
      }
    };

    const onScroll = () => {
      const shouldStick = window.scrollY + headerHeight + 1 >= navTop;
      const isSticky = nav.classList.contains('is-sticky');
      if (shouldStick && !isSticky) {
        nav.classList.add('is-sticky');
        placeholder.style.display = 'block';
      } else if (!shouldStick && isSticky) {
        nav.classList.remove('is-sticky');
        placeholder.style.display = 'none';
      }
    };

    const onResize = () => {
      headerHeight = measureHeader();
      measure();
      onScroll();
    };

    measure();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    stickyState = { nav, placeholder, onScroll, onResize };
  };

  // Smooth accordion expand/collapse using max-height based on scrollHeight.
  // Uses native <details>/<summary> so keyboard + screen reader support is free.
  const initAccordion = () => {
    document.querySelectorAll('details.la-docs-qa').forEach((d) => {
      if (d.dataset.laAccordion) return;
      d.dataset.laAccordion = '1';
      const answer = d.querySelector('.la-docs-qa-answer');
      const inner = d.querySelector('.la-docs-qa-answer-inner');
      if (!answer || !inner) return;

      const setClosed = () => {
        answer.style.maxHeight = '0px';
      };
      const setOpen = () => {
        answer.style.maxHeight = inner.scrollHeight + 'px';
      };

      // Initial: if [open], set max-height; else 0
      if (d.hasAttribute('open')) setOpen(); else setClosed();

      const summary = d.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = d.hasAttribute('open');
        if (isOpen) {
          // Closing: animate height back to 0, then remove [open]
          answer.style.maxHeight = inner.scrollHeight + 'px';
          requestAnimationFrame(() => { answer.style.maxHeight = '0px'; });
          const onEnd = () => {
            d.removeAttribute('open');
            answer.removeEventListener('transitionend', onEnd);
          };
          answer.addEventListener('transitionend', onEnd);
        } else {
          // Opening: add [open], then animate to scrollHeight
          d.setAttribute('open', '');
          answer.style.maxHeight = '0px';
          requestAnimationFrame(() => { answer.style.maxHeight = inner.scrollHeight + 'px'; });
          const onEnd = () => {
            answer.style.maxHeight = 'none';
            answer.removeEventListener('transitionend', onEnd);
          };
          answer.addEventListener('transitionend', onEnd);
        }
      });
    });

    // If the URL hash matches an anchor inside the list, open that Q&A.
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target && target.closest && target.closest('details.la-docs-qa')) {
        const d = target.closest('details.la-docs-qa');
        d.setAttribute('open', '');
        const answer = d.querySelector('.la-docs-qa-answer');
        const inner = d.querySelector('.la-docs-qa-answer-inner');
        if (answer && inner) answer.style.maxHeight = inner.scrollHeight + 'px';
      }
    }
  };

  // Copy-to-clipboard on Q&A link buttons — copies the canonical URL with
  // the question's anchor so CS can paste a deep link to a specific Q&A.
  const initQaLinkButtons = () => {
    document.querySelectorAll('.la-docs-qa-link').forEach((btn) => {
      if (btn.dataset.laQaLinkReady) return;
      btn.dataset.laQaLinkReady = '1';
      // Block clicks and key activations from bubbling to <summary>, which
      // would toggle the <details>.
      const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
      btn.addEventListener('mousedown', swallow);
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') swallow(e);
      });
      btn.addEventListener('click', async (e) => {
        swallow(e);
        const anchor = btn.getAttribute('data-anchor');
        if (!anchor) return;
        const url = window.location.origin + window.location.pathname + '#' + anchor;
        try {
          await navigator.clipboard.writeText(url);
        } catch (_) {
          // Fallback for non-secure contexts: use a hidden textarea.
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); } catch (_) {}
          document.body.removeChild(ta);
        }
        btn.classList.add('is-copied');
        clearTimeout(btn._laCopiedTimer);
        btn._laCopiedTimer = setTimeout(() => btn.classList.remove('is-copied'), 1600);
      });
    });
  };

  // Copy-to-clipboard on code blocks
  const initCopyButtons = () => {
    document.querySelectorAll('.la-docs-code').forEach((block) => {
      if (block.dataset.laCopyReady) return;
      block.dataset.laCopyReady = '1';
      const btn = block.querySelector('.la-docs-code-copy');
      const pre = block.querySelector('pre');
      if (!btn || !pre) return;
      btn.addEventListener('click', async () => {
        const text = pre.textContent;
        try {
          await navigator.clipboard.writeText(text);
          const original = btn.textContent;
          btn.textContent = 'Copied';
          btn.classList.add('is-copied');
          setTimeout(() => {
            btn.textContent = original;
            btn.classList.remove('is-copied');
          }, 1600);
        } catch (_) {
          // Clipboard API not available; ignore.
        }
      });
    });
  };

  const initReveal = () => {
    const roots = document.querySelectorAll('.la-docs-page');
    if (!roots.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const targets = [];
    roots.forEach((root) => {
      root.querySelectorAll('.la-docs-head, .la-docs-faq-head, .la-docs-content-head, .la-docs-labs-head, details.la-docs-qa, .la-docs-content-block, .la-docs-lab, .la-docs-close-inner > div').forEach((el) => {
        if (!el.dataset.laReveal) {
          el.dataset.laReveal = '1';
          targets.push(el);
        }
      });
    });
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.animate(
          [
            { opacity: 0, transform: 'translateY(12px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 550, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' }
        );
        io.unobserve(e.target);
      });
    }, { threshold: 0.1 });
    targets.forEach((el) => { el.style.opacity = '0'; io.observe(el); });
  };

  const init = () => {
    initStickyNav();
    initAccordion();
    initQaLinkButtons();
    initCopyButtons();
    initReveal();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
